<template>
  <div class="table__container">
    <header class="header">
      <div class="top_controls">
        <div class="searchbar_container">
          <SearchBar
            ref="searchbar"
            class="searchbar"
            :placeholder="searchbarPlaceholder"
            v-if="(searchable && !empty) || searching"
            @input="
              $emit('search', {
                term: $event,
                columns: searchableColumns,
              })
            "
          />
        </div>
      </div>
    </header>
    <hr class="divider" />
    <SearchTreeView
      v-if="!empty"
      :items="treeData"
      :onClickHandler="selectItemHandler"
    />
    <div v-else class="empty">
      <slot name="empty"></slot>
    </div>
    <div class="footer" v-if="paginate">
      <RowsPerPage
        :text="rowsPerPageText"
        :defaultPaginationSize="defaultPaginationSize"
        :paginationSizeOptions="paginationSizeOptions"
        @onPaginationSizeChange="$emit('onPaginationSizeChange', $event)"
      />
      <div class="pagination_indicator">
        <slot name="pagination-indicator">
          <span>{{ `Page ${currentPage} of ${pages}` }}</span>
        </slot>
      </div>
      <div v-if="pages > 1" class="footer__buttons">
        <Pagination
          :pageIndex="currentPage"
          :pageCount="pages"
          @pageChange="$emit('pageChange', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { faCaretDown, faCaretUp } from "@fortawesome/pro-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import Pagination from "./Pagination.vue";
import SearchBar from "../atoms/Inputs/SearchBar.vue";
import RowsPerPage from "../atoms/RowsPerPage.vue";
import SearchTreeView from "./SearchTreeView.vue";

library.add(faCaretUp, faCaretDown);

export default {
  name: "SearchTree",
  components: {
    SearchBar,
    Pagination,
    RowsPerPage,
    SearchTreeView,
  },
  props: {
    /**
     * Flag to indicate whether the table should render. When true a placeholder text is displayed.
     */
    empty: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, a filter like search input is displayed in the header part if the card.
     */
    searchable: {
      type: Boolean,
      default: false,
    },
    /**
     * To indicate that the current data present in table is the product of a filter/search result.
     */
    searching: {
      type: Boolean,
      default: false,
    },
    /**
     * Placeholder text to be displayed in the searchbar if present
     */
    searchbarPlaceholder: {
      type: String,
      default: "Search",
    },
    /**
     * Flag to indicate the data in the table is paginated. Will render page controls in footer and emit page navigation events.
     */
    paginate: {
      type: Boolean,
      default: true,
    },
    /**
     * Current page
     */
    currentPage: {
      type: Number,
    },
    /**
     * Total amount of pages.
     */
    pages: {
      type: Number,
    },
    rowsPerPageText: {
      type: String,
    },
    defaultPaginationSize: {
      type: Number,
    },
    paginationSizeOptions: {
      type: Array,
    },
    treeData: {
      type: Array,
    },
    searchableColumns: {
      type: Array,
      default: () => ["name", "children.name"],
    },
    selectItemHandler: {
      type: Function,
      required: true,
    },
  },
  methods: {
    clearSearchbar() {
      this.$refs.searchbar && this.$refs.searchbar.clearSearchbar();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~include-media";
@import "../../style_variables/colors";
@import "../../style_variables/typography_classes";
@import "../../style_variables/breakpoints";
$open_sans: "Open Sans", sans-serif;

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px 20px 24px;

  @include media("<tablet") {
    padding: 0 16px 20px 16px;
  }
  .top_controls {
    width: 100%;

    .searchbar {
      width: 100%;
    }
  }
}

.divider {
  border: none;
  border-top: 1px solid #f2f2f7;
  margin: 0 24px;

  @include media("<tablet") {
    margin: 0 16px;
  }
}

.table__container {
  padding: 28px 0 24px 0;
  background-color: $white;
  border-radius: 8px;
  border: 1px solid $grey_alabaster;

  @include media($isPhone...) {
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
}

.footer {
  font-family: $open_sans;
  font-size: 0.875rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px 0 24px;
  @include media("<tablet") {
    padding: 0 16px;
  }
}

.footer__buttons {
  display: flex;
}

.empty {
  font-family: $open_sans;
  font-size: 0.75rem;
  color: $grey_manatee;
  font-weight: 600;
  padding-top: 16px;
}

.pagination_indicator {
  margin-left: auto;
  margin-right: 20px;
}
</style>
